// The keys of end-to-end credentials on tilez-cloud (docs/specs/secrets.md SE-11 to SE-14), all in
// Web Crypto, which every shell's engine has:
// - a space's key (AES-256-GCM) encrypts each credential, bound to its space, its name and the key's version;
// - each device's ECDH P-256 key pair receives the space key wrapped for it: an ephemeral key pair,
//   HKDF-SHA-256 over the shared secret, AES-GCM;
// - the recovery key, 128 random bits shown as letters and digits, wraps the space key too.
// tilez-cloud keeps only what these functions produce; it never gets a key it can open them with.

const FORMAT = 1;
const NONCE_BYTES = 12;
const RECOVERY_BYTES = 16;
const P256 = { name: 'ECDH', namedCurve: 'P-256' } as const;

/** What a value, or a key, sealed with AES-GCM is kept as: base64. */
export interface Sealed {
    nonce: string;
    data: string;
}

/** A space key wrapped for one device: the ephemeral public key with it. */
export interface WrappedForDevice extends Sealed {
    ephemeral: string;
}

/** Where a credential belongs, bound to its ciphertext so that it cannot be moved unnoticed (SE-11). */
export interface SecretContext {
    space: string;
    keyVersion: number;
    name: string;
}

function base64Of(bytes: ArrayBuffer | Uint8Array) : string {
    let text = '';

    for (const byte of new Uint8Array(bytes)) {
        text += String.fromCharCode(byte);
    }

    return btoa(text);
}

function bytesOf(base64: string) : Uint8Array<ArrayBuffer> {
    return Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
}

const utf8 = (text: string) : Uint8Array<ArrayBuffer> => new TextEncoder().encode(text);

async function seal(key: CryptoKey, plain: Uint8Array<ArrayBuffer>, boundTo: string) : Promise<Sealed> {
    const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
    const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: utf8(boundTo) }, key, plain);

    return { nonce: base64Of(nonce), data: base64Of(data) };
}

/** The plain bytes; throws when the data or what it is bound to was changed, or the key is another. */
async function open(key: CryptoKey, sealed: Sealed, boundTo: string) : Promise<ArrayBuffer> {
    return crypto.subtle.decrypt({ name: 'AES-GCM', iv: bytesOf(sealed.nonce), additionalData: utf8(boundTo) }, key, bytesOf(sealed.data));
}

/** A new space key (SE-11). Extractable: any device of the space may wrap it for a new one (QSE-4). */
export function makeSpaceKey() : Promise<CryptoKey> {
    return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

const secretBinding = (context: SecretContext) : string => `casa-secret/${FORMAT}/${context.space}/${context.keyVersion}/${context.name}`;

export function encryptSecret(spaceKey: CryptoKey, value: string, context: SecretContext) : Promise<Sealed> {
    return seal(spaceKey, utf8(value), secretBinding(context));
}

export async function decryptSecret(spaceKey: CryptoKey, sealed: Sealed, context: SecretContext) : Promise<string> {
    return new TextDecoder().decode(await open(spaceKey, sealed, secretBinding(context)));
}

/** This device's key pair (SE-12): the private half cannot be read out, the public half is shared. */
export function makeDeviceKeys() : Promise<CryptoKeyPair> {
    return crypto.subtle.generateKey(P256, false, ['deriveKey']);
}

/** A public key as the server and the QR code carry it: base64 of its raw point. */
export async function exportPublicKey(publicKey: CryptoKey) : Promise<string> {
    return base64Of(await crypto.subtle.exportKey('raw', publicKey));
}

function importPublicKey(base64: string) : Promise<CryptoKey> {
    return crypto.subtle.importKey('raw', bytesOf(base64), P256, true, []);
}

/** The AES key two ECDH halves agree on, through HKDF-SHA-256. */
async function agreedKey(privateKey: CryptoKey, publicKey: CryptoKey, usages: KeyUsage[]) : Promise<CryptoKey> {
    const shared = await crypto.subtle.deriveKey({ name: 'ECDH', public: publicKey }, privateKey, { name: 'HKDF' }, false, ['deriveKey']);

    return crypto.subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: utf8(`casa-space-key/${FORMAT}`) }, shared, { name: 'AES-GCM', length: 256 }, false, usages);
}

const wrapBinding = (space: string, keyVersion: number) : string => `casa-space-key/${FORMAT}/${space}/${keyVersion}`;

/** The space key, wrapped for the device whose public key this is (SE-12, SE-13). */
export async function wrapForDevice(spaceKey: CryptoKey, devicePublicKey: string, space: string, keyVersion: number) : Promise<WrappedForDevice> {
    const ephemeral = await crypto.subtle.generateKey(P256, true, ['deriveKey']);
    const key = await agreedKey(ephemeral.privateKey, await importPublicKey(devicePublicKey), ['encrypt']);
    const raw = new Uint8Array(await crypto.subtle.exportKey('raw', spaceKey));

    return { ...(await seal(key, raw, wrapBinding(space, keyVersion))), ephemeral: await exportPublicKey(ephemeral.publicKey) };
}

/** The space key, from what was wrapped for this device. */
export async function unwrapForDevice(wrapped: WrappedForDevice, deviceKeys: CryptoKeyPair, space: string, keyVersion: number) : Promise<CryptoKey> {
    const key = await agreedKey(deviceKeys.privateKey, await importPublicKey(wrapped.ephemeral), ['decrypt']);
    const raw = await open(key, wrapped, wrapBinding(space, keyVersion));

    return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}

// The recovery key as people read and type it: Crockford's base32 (no I, L, O or U), in groups of
// four. 128 bits are 26 characters.
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** A new recovery key (SE-14). */
export function makeRecoveryKey() : Uint8Array<ArrayBuffer> {
    return crypto.getRandomValues(new Uint8Array(RECOVERY_BYTES));
}

export function recoveryKeyText(bytes: Uint8Array) : string {
    let bits = 0;
    let value = 0;
    let text = '';

    for (const byte of bytes) {
        value = (value << 8) | byte;
        bits += 8;

        while (bits >= 5) {
            text += ALPHABET[(value >>> (bits - 5)) & 31];
            bits -= 5;
        }
    }

    if (bits > 0) {
        text += ALPHABET[(value << (5 - bits)) & 31];
    }

    return text.match(/.{1,4}/g)?.join('-') ?? text;
}

/** The recovery key's bytes from what was typed: case, spaces and dashes do not matter, nor O for 0 or I and L for 1. Undefined when it is not one. */
export function parseRecoveryKey(typed: string) : Uint8Array<ArrayBuffer> | undefined {
    const text = typed.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
    const expected = Math.ceil((RECOVERY_BYTES * 8) / 5);

    if (text.length !== expected || [...text].some((character) => !ALPHABET.includes(character))) {
        return undefined;
    }

    const bytes = new Uint8Array(RECOVERY_BYTES);
    let bits = 0;
    let value = 0;
    let index = 0;

    for (const character of text) {
        value = (value << 5) | ALPHABET.indexOf(character);
        bits += 5;

        if (bits >= 8 && index < RECOVERY_BYTES) {
            bytes[index++] = (value >>> (bits - 8)) & 255;
            bits -= 8;
        }
    }

    return bytes;
}

async function recoveryWrappingKey(recoveryKey: Uint8Array<ArrayBuffer>, usages: KeyUsage[]) : Promise<CryptoKey> {
    const material = await crypto.subtle.importKey('raw', recoveryKey, { name: 'HKDF' }, false, ['deriveKey']);

    return crypto.subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: utf8(`casa-recovery/${FORMAT}`) }, material, { name: 'AES-GCM', length: 256 }, false, usages);
}

/** The space key, wrapped with the recovery key (SE-14). */
export async function wrapForRecovery(spaceKey: CryptoKey, recoveryKey: Uint8Array<ArrayBuffer>, space: string, keyVersion: number) : Promise<Sealed> {
    const raw = new Uint8Array(await crypto.subtle.exportKey('raw', spaceKey));

    return seal(await recoveryWrappingKey(recoveryKey, ['encrypt']), raw, wrapBinding(space, keyVersion));
}

/** The space key, from the recovery key; throws when it is not the one it was wrapped with. */
export async function unwrapForRecovery(wrapped: Sealed, recoveryKey: Uint8Array<ArrayBuffer>, space: string, keyVersion: number) : Promise<CryptoKey> {
    const raw = await open(await recoveryWrappingKey(recoveryKey, ['decrypt']), wrapped, wrapBinding(space, keyVersion));

    return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}

/**
 * A space's key as people type it (cloud-spaces SP-3, the owner: "no longer than 8 characters"):
 * 40 random bits as XXXX-XXXX. It only lets a device into its space, where guesses are counted
 * (tilez-cloud's limit per address); it opens nothing, so its shortness gives nothing away offline.
 */
export function makeJoinKey() : string {
    return recoveryKeyText(crypto.getRandomValues(new Uint8Array(5)));
}

/** A join key as typed, normalised (case, spaces, dashes, O for 0, I and L for 1); undefined when it is not one. */
export function parseJoinKey(typed: string) : string | undefined {
    const text = typed.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');

    return /^[0-9A-HJKMNP-TV-Z]{8}$/.test(text) ? `${text.slice(0, 4)}-${text.slice(4)}` : undefined;
}

/** A public key's short code, to compare on two screens when linking (SE-13): 8 characters of its SHA-256. */
export async function fingerprintOf(publicKey: string) : Promise<string> {
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytesOf(publicKey)));

    return recoveryKeyText(digest.slice(0, 5)).slice(0, 9);
}
